// F18-R9 — Cosmetic preview page.
// Shows how a cosmetic looks on leaderboard and profile.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi, getErrorMessage, type Cosmetic } from '../lib/api';

export default function CosmeticPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cosmetic, setCosmetic] = useState<Cosmetic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const data = await adminApi.listCosmetics();
        const found = data.cosmetics.find((c) => c.id === decodeURIComponent(id));
        if (!found) {
          setError('Cosmetic not found');
        } else {
          setCosmetic(found);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-4 text-gray-400">Loading cosmetic...</div>;
  if (error) return <div className="p-4 text-red-400">{error}</div>;
  if (!cosmetic) return <div className="p-4 text-gray-400">Cosmetic not found</div>;

  const accentColor = `#${(cosmetic.accent_argb & 0xFFFFFF).toString(16).padStart(6, '0')}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/cosmetics')}
          className="text-gray-400 hover:text-white"
        >&larr; Back to Cosmetics</button>
        <h2 className="text-xl font-bold text-white">Cosmetic Preview: {cosmetic.id}</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Leaderboard Mockup */}
        <div className="rounded-lg border border-gray-700 bg-gray-900 p-6">
          <h3 className="mb-4 text-sm font-medium text-gray-400">Leaderboard Appearance</h3>

          <div className="rounded-xl bg-gray-950 p-4">
            {/* Simulated leaderboard row with cosmetic applied */}
            <div
              className="flex items-center gap-3 rounded-lg p-3"
              style={{
                background: cosmetic.type === 'leaderboard_background'
                  ? `linear-gradient(90deg, ${accentColor}15, ${accentColor}05)`
                  : 'transparent',
              }}
            >
              {/* Rank badge */}
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
                style={{
                  background: cosmetic.type === 'rank_badge' ? `${accentColor}25` : '#333',
                  color: '#fff',
                }}
              >
                {cosmetic.type === 'rank_badge' ? cosmetic.emoji : '1'}
              </div>

              {/* Avatar with frame */}
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-700 text-white"
                style={{
                  border: cosmetic.type === 'avatar_frame' ? `2.5px solid ${accentColor}` : 'none',
                }}
              >
                👤
              </div>

              {/* Nickname with style/frame */}
              <div className="flex-1">
                <span
                  style={{
                    color: cosmetic.type === 'name_style' ? accentColor : '#fff',
                    fontWeight: 600,
                    ...(cosmetic.type === 'name_frame' ? {
                      padding: '2px 6px',
                      border: `1.5px solid ${accentColor}99`,
                      borderRadius: '4px',
                    } : {}),
                  }}
                >
                  {cosmetic.type === 'title_prefix' ? `${cosmetic.emoji} ` : ''}
                  SampleUser
                </span>
              </div>

              {/* Score */}
              <span className="text-sm font-bold text-orange-400">12,450</span>
            </div>

            {/* Without cosmetic (comparison) */}
            <div className="mt-2 flex items-center gap-3 rounded-lg p-3 opacity-50">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-800 text-sm font-bold text-white">2</div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-700 text-white">👤</div>
              <div className="flex-1">
                <span className="font-semibold text-white">RegularUser</span>
              </div>
              <span className="text-sm font-bold text-gray-500">9,820</span>
            </div>
          </div>
        </div>

        {/* Profile Mockup */}
        <div className="rounded-lg border border-gray-700 bg-gray-900 p-6">
          <h3 className="mb-4 text-sm font-medium text-gray-400">Profile Appearance</h3>

          <div className="rounded-xl bg-gray-950 p-4">
            <div className="flex items-center gap-4">
              {/* Avatar with frame */}
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-700 text-2xl text-white"
                style={{
                  border: cosmetic.type === 'avatar_frame' ? `3px solid ${accentColor}` : 'none',
                }}
              >
                👤
              </div>
              <div>
                <div
                  style={{
                    color: cosmetic.type === 'name_style' ? accentColor : '#fff',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    ...(cosmetic.type === 'name_frame' ? {
                      padding: '2px 8px',
                      border: `2px solid ${accentColor}99`,
                      borderRadius: '6px',
                      display: 'inline-block',
                    } : {}),
                  }}
                >
                  {cosmetic.type === 'title_prefix' ? `${cosmetic.emoji} ` : ''}
                  SampleUser
                </div>
                <div className="text-xs text-gray-500">Level 42 · Gourmet Score 8,530</div>
              </div>
            </div>

            {/* Cosmetic chip */}
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-gray-900 p-3">
              <span className="text-xl">{cosmetic.emoji}</span>
              <div>
                <div className="text-sm text-white" style={{ color: accentColor }}>
                  {cosmetic.id.replace(/showoff_/g, '').replace(/_/g, ' ')}
                </div>
                <div className="text-xs text-gray-500">{cosmetic.type} · {cosmetic.display_context}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cosmetic metadata */}
      <div className="rounded border border-gray-700 bg-gray-850 p-4">
        <h3 className="mb-3 text-sm font-medium text-gray-400">Cosmetic Details</h3>
        <dl className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
          <div><dt className="text-gray-500">ID:</dt> <dd className="font-mono text-gray-300">{cosmetic.id}</dd></div>
          <div><dt className="text-gray-500">Type:</dt> <dd className="text-gray-300">{cosmetic.type}</dd></div>
          <div><dt className="text-gray-500">Emoji:</dt> <dd className="text-gray-300">{cosmetic.emoji}</dd></div>
          <div><dt className="text-gray-500">Acquire:</dt> <dd className="text-gray-300">{cosmetic.acquire}</dd></div>
          <div><dt className="text-gray-500">Display:</dt> <dd className="text-gray-300">{cosmetic.display_context}</dd></div>
          <div><dt className="text-gray-500">Premium:</dt> <dd className="text-gray-300">{cosmetic.premium_only ? 'Yes' : 'No'}</dd></div>
          <div><dt className="text-gray-500">Stock:</dt> <dd className="text-gray-300">{cosmetic.stock_sold}{cosmetic.stock_limit ? `/${cosmetic.stock_limit}` : ''}</dd></div>
          <div><dt className="text-gray-500">Active:</dt> <dd className="text-gray-300">{cosmetic.is_active ? 'Yes' : 'No'}</dd></div>
        </dl>
      </div>
    </div>
  );
}
